// Entity resolution scoring (PLAN §5 step 5, §6.1; task 17). For an extracted entity and an existing one, how
// alike are their names (normalized, transliterated, phonetic, spelling-variant and abjad-skeleton comparison, any
// script), how much context do they share (linked actors, places, years, type), and do reviewer-confirmed name
// equivalents apply? The result is a score in 0–1 with its evidence.
//
// Rules the pipeline and review rely on:
//  - Score ≥ PRESELECT_THRESHOLD is *pre-selected* in review, never committed on its own.
//  - A match across scripts or languages (Hasan ↔ حسن) is never pre-selected, whatever the score: it always goes
//    to a "Same person?" confirmation.
//  - Name equivalents raise a score; they never confirm an identity on their own.
// Pure functions only: candidate generation (D1, FTS, vectors) lives in the claims service and the pipeline.

import { detectScript, type NameScript, nameForms, normalizeName } from './names';

/** Suggestions at or above this score are pre-selected (same script and language only). */
export const PRESELECT_THRESHOLD = 0.92;
/** Below this, an existing entity is not suggested at all. */
export const SUGGEST_THRESHOLD = 0.55;
/** Suggestions kept per extracted entity. */
export const MAX_SUGGESTIONS = 5;
/** Cross-script suggestions at or above this become "Same person?" items. */
export const CONFIRM_THRESHOLD = 0.7;

/** Scripts written without short vowels: their Latin transliteration is a consonant skeleton. */
const ABJADS: ReadonlySet<NameScript> = new Set(['Arab', 'Hebr']);
const BASE_SCRIPTS = ['Latn', 'Arab', 'Hebr', 'Cyrl', 'Grek', 'Hani'] as const;

/** Name particles ignored when aligning words ("al", "von", "de"…) unless they are the whole name. */
const PARTICLES = new Set([
  'al',
  'el',
  'ad',
  'as',
  'at',
  'ar',
  'an',
  'de',
  'da',
  'di',
  'du',
  'des',
  'del',
  'la',
  'le',
  'van',
  'von',
  'der',
  'den',
  'the',
  'ibn',
  'bin',
  'ben',
]);
/** Organization words skipped when building an acronym. */
const ACRONYM_SKIP = new Set(['of', 'for', 'and', 'the', 'de', 'des', 'du', 'la', 'le', 'et', 'pour', 'on', 'in', 'to']);

/** Every letter script present in a name (a "Mixed" name like "Hasan Haddad (حسن حداد)" has two). */
export function scriptsOf(value: string): Set<string> {
  const out = new Set<string>();
  for (const ch of value) {
    if (!/\p{L}/u.test(ch)) continue;
    const s = detectScript(ch);
    out.add(BASE_SCRIPTS.includes(s as (typeof BASE_SCRIPTS)[number]) ? s : 'Other');
  }
  return out;
}

/**
 * Two names are cross-script when they share no script ("Hasan" / "حسن"), and cross-language when both
 * languages are known, differ, and the names aren't written the same ("European Commission" / "Commission européenne").
 */
export function isCrossScript(a: { value: string; lang?: string | null }, b: { value: string; lang?: string | null }): boolean {
  const sa = scriptsOf(a.value);
  const sb = scriptsOf(b.value);
  if (sa.size && sb.size && ![...sa].some(s => sb.has(s))) return true;
  const la = a.lang?.toLowerCase().split('-')[0];
  const lb = b.lang?.toLowerCase().split('-')[0];
  return !!la && !!lb && la !== lb && normalizeName(a.value) !== normalizeName(b.value);
}

/**
 * Two names written in scripts they don't share ("Hasan" / "حسن"). Unlike isCrossScript, two Latin names in
 * different languages ("Allemagne" / "Germany") are not: the "Same person?" queue is for names in other scripts.
 */
export function isOtherScript(a: string, b: string): boolean {
  return isCrossScript({ value: a }, { value: b });
}

/** Jaro–Winkler similarity (0–1). */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const window = Math.max(0, Math.floor(Math.max(a.length, b.length) / 2) - 1);
  const am = new Array<boolean>(a.length).fill(false);
  const bm = new Array<boolean>(b.length).fill(false);
  let m = 0;
  for (let i = 0; i < a.length; i++) {
    for (let j = Math.max(0, i - window); j < Math.min(b.length, i + window + 1); j++) {
      if (bm[j] || a[i] !== b[j]) continue;
      am[i] = bm[j] = true;
      m++;
      break;
    }
  }
  if (!m) return 0;
  let t = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!am[i]) continue;
    while (!bm[k]) k++;
    if (a[i] !== b[k]) t++;
    k++;
  }
  const jaro = (m / a.length + m / b.length + (m - t / 2) / m) / 3;
  let prefix = 0;
  while (prefix < 4 && a[prefix] === b[prefix]) prefix++;
  return jaro + prefix * 0.1 * (1 - jaro);
}

/** Letter classes for comparing an abjad skeleton with a Latin spelling (vowel letters by sound, digraphs merged). */
function skeletonClasses(w: string): string {
  return w
    .replace(/sch|sh|ch/g, 'S')
    .replace(/th|dh/g, 'T')
    .replace(/kh|ck|q|c|k/g, 'K')
    .replace(/gh|g/g, 'G')
    .replace(/ph|f/g, 'F')
    .replace(/[td]/g, m => (m === 't' ? 'T' : 'D'))
    .replace(/[sz]/g, 'S')
    .replace(/[ouwv]/g, 'U')
    .replace(/[eiyj]/g, 'I')
    .replace(/a/g, 'A');
}

/**
 * Whether an abjad transliteration ("hdad", written consonants and long vowels only) fits inside a Latin
 * spelling ("haddad") letter by letter in order. Tells "Haddad" (حداد) from "Hadid" (حديد), which sound alike.
 */
export function abjadFits(skeleton: string, latin: string): boolean {
  if (skeleton.replace(/[aeiou]/g, '').length < 2) return false;
  let s = skeletonClasses(skeleton);
  let l = skeletonClasses(latin);
  // A word-initial ع/ا is written "a" but can be any vowel in Latin ("Omar" / عمر).
  if (s.startsWith('A') && /^[AIU]/.test(l)) {
    s = s.slice(1);
    l = l.replace(/^[AIU]+/, '');
  }
  let i = 0;
  for (const ch of l) if (ch === s[i]) i++;
  return i === s.length;
}

function phoneticWord(w: string): string {
  // Same key as names.phoneticKey, per word.
  return nameForms(w).phoneticKey;
}

interface Word {
  /** Normalized, in its own script (for name equivalents). */
  norm: string;
  /** Latin form (transliterated when needed). */
  latin: string;
  phonetic: string;
  /** Written in an abjad: `latin` is a consonant skeleton. */
  abjad: boolean;
  initial: boolean;
}

/**
 * Bounded memo for pure per-name work: name forms (transliteration, sounds) are the expensive part of every
 * comparison, and the same names come back thousands of times while a document is matched (a stage compares each
 * extracted entity's context with the context of up to 25 existing entities).
 */
function memo<V>(limit: number): (key: string, compute: () => V) => V {
  const m = new Map<string, V>();
  return (key, compute) => {
    const had = m.get(key);
    if (had !== undefined) return had;
    if (m.size >= limit) m.clear();
    const v = compute();
    m.set(key, v);
    return v;
  };
}

interface Words {
  words: readonly Word[];
  latin: string;
  abjad: boolean;
}
const wordsMemo = memo<Words>(50_000);
const keysMemo = memo<readonly string[]>(50_000);

/** A name's words with their Latin forms (particles dropped unless that leaves nothing). Memoized. */
function wordsOf(value: string): Words {
  return wordsMemo(value, () => splitWords(value));
}

function splitWords(value: string): Words {
  const all: Word[] = normalizeName(value)
    .split(' ')
    .filter(Boolean)
    .map(norm => {
      const f = nameForms(norm);
      const abjad = ABJADS.has(f.script);
      let latin = f.latinTranslit ?? f.normalized;
      // Arabic "al-" is often joined to the word in writing ("الحسن" → "alhsn"): compare without it.
      if (abjad && latin.startsWith('al') && latin.length > 4) latin = latin.slice(2);
      return { norm, latin, phonetic: phoneticWord(latin), abjad, initial: latin.length === 1 };
    });
  const kept = all.filter(w => !PARTICLES.has(w.latin));
  const words = kept.length ? kept : all;
  return { words, latin: words.map(w => w.latin).join(' '), abjad: words.some(w => w.abjad) };
}

/** How alike two words are (0–1), and how. */
function wordSimilarity(a: Word, b: Word): { score: number; via: MatchVia } {
  if (a.latin === b.latin) return { score: 1, via: 'exact' };
  if (a.initial || b.initial) return a.latin[0] === b.latin[0] ? { score: 0.7, via: 'initial' } : { score: 0, via: 'none' };
  if (a.abjad !== b.abjad) {
    const [sk, la] = a.abjad ? [a.latin, b.latin] : [b.latin, a.latin];
    if (abjadFits(sk, la)) return { score: 0.95, via: 'transliteration' };
  }
  const jw = jaroWinkler(a.latin, b.latin);
  const spelled = jw >= 0.9 ? jw * 0.95 : jw * 0.8;
  // Sound-alike keys are weak evidence when short ("hd" is Haddad and Hadid alike).
  const sound = a.phonetic && a.phonetic === b.phonetic ? (a.phonetic.length >= 3 ? 0.86 : 0.75) : 0;
  // Two abjad-vs-Latin words that don't fit letter by letter are different names, however alike they sound.
  const capped = a.abjad !== b.abjad ? Math.min(sound, 0.72) : sound;
  return spelled >= capped ? { score: spelled, via: 'spelling' } : { score: capped, via: 'phonetic' };
}

export type MatchVia =
  | 'exact'
  | 'normalized'
  | 'transliteration'
  | 'spelling'
  | 'phonetic'
  | 'initial'
  | 'partial'
  | 'acronym'
  | 'equivalent'
  | 'vector'
  | 'none';

export interface NameMatch {
  score: number;
  via: MatchVia;
  /** Only part of the longer name matched ("Woitchik" / "Alain Woitchik"). */
  partial: boolean;
}

function acronymOf(latin: string): string {
  return latin
    .split(' ')
    .filter(w => w && !ACRONYM_SKIP.has(w))
    .map(w => w[0])
    .join('');
}

/** Pairs of reviewer-confirmed equivalent spellings, keyed by `pairKey` of their normalized forms. */
export type EquivalentIndex = ReadonlyMap<string, number>;
/** Order-independent key of two normalized names. */
export const pairKey = (a: string, b: string) => (a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`);

/**
 * Similarity of two names in any scripts. Words are aligned regardless of order; unmatched words of the longer
 * name lower the score; an incomplete name (every word of the shorter one matched) scores as a partial match.
 */
export function nameSimilarity(a: string, b: string, equivalents?: EquivalentIndex): NameMatch {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return { score: 0, via: 'none', partial: false };
  if (na === nb) return { score: 1, via: a.trim() === b.trim() ? 'exact' : 'normalized', partial: false };
  const equivalent = equivalents?.get(pairKey(na, nb));

  const A = wordsOf(a);
  const B = wordsOf(b);
  if (A.latin === B.latin) {
    const sameScripts = [...scriptsOf(a)].sort().join() === [...scriptsOf(b)].sort().join();
    return { score: 1, via: sameScripts ? 'normalized' : 'transliteration', partial: false };
  }

  // Word-by-word equivalents ("Hasan" ↔ "حسن") count as a transliteration of that word.
  const eqWord = (x: Word, y: Word) => !!equivalents?.has(pairKey(x.norm, y.norm));
  const [short, long] = A.words.length <= B.words.length ? [A.words, B.words] : [B.words, A.words];
  const used = new Set<number>();
  let sum = 0;
  let matched = 0;
  const vias: MatchVia[] = [];
  for (const w of short) {
    let best = { score: 0, via: 'none' as MatchVia, j: -1 };
    long.forEach((x, j) => {
      if (used.has(j)) return;
      const s = eqWord(w, x) ? { score: 0.97, via: 'equivalent' as MatchVia } : wordSimilarity(w, x);
      if (s.score > best.score) best = { ...s, j };
    });
    if (best.j >= 0 && best.score >= 0.6) {
      used.add(best.j);
      sum += best.score;
      if (best.score >= 0.85) matched++;
      vias.push(best.via);
    }
  }
  let score = sum / Math.max(long.length, 1);
  let partial = false;
  if (long.length > short.length && matched === short.length && short.length > 0) {
    // An incomplete name: suggested, never strong on its own.
    const avg = sum / short.length;
    if (avg * 0.72 > score) {
      score = avg * 0.72;
      partial = true;
    }
  }

  // Joined forms: "Abdullah" / "عبد الله", "Al Hasan" / "Alhasan".
  const ja = A.words.map(w => w.latin).join('');
  const jb = B.words.map(w => w.latin).join('');
  if (A.words.length !== B.words.length && Math.min(ja.length, jb.length) / Math.max(ja.length, jb.length) >= 0.7) {
    const joined = wordSimilarity(
      { norm: ja, latin: ja, phonetic: phoneticWord(ja), abjad: A.abjad, initial: false },
      { norm: jb, latin: jb, phonetic: phoneticWord(jb), abjad: B.abjad, initial: false },
    );
    // Only word breaks differ: strong. Otherwise one name has an extra word ("MEP", "Jr"): never pre-selection strong.
    const joinedScore = ja === jb ? 0.97 : Math.min(joined.score, 0.9);
    if (joinedScore > score) {
      score = joinedScore;
      partial = false;
      vias.splice(0, vias.length, joined.via);
    }
  }

  // Acronyms: "ELNET" / "European Leadership Network".
  const acA = A.words.length === 1 ? A.words[0]?.latin : null;
  const acB = B.words.length === 1 ? B.words[0]?.latin : null;
  let via: MatchVia = vias.includes('equivalent')
    ? 'equivalent'
    : vias.includes('transliteration')
      ? 'transliteration'
      : vias.includes('phonetic')
        ? 'phonetic'
        : vias.includes('initial')
          ? 'initial'
          : vias.length
            ? vias.every(v => v === 'exact')
              ? 'normalized'
              : 'spelling'
            : 'none';
  if (partial) via = 'partial';
  for (const [ac, other] of [
    [acA, B],
    [acB, A],
  ] as const) {
    if (ac && ac.length >= 2 && other.words.length >= 2 && acronymOf(other.latin) === ac && score < 0.8) {
      score = 0.8;
      via = 'acronym';
      partial = false;
    }
  }

  if (equivalent !== undefined) {
    // A confirmed pair raises the score (a little more for pairs confirmed often), still below pre-selection alone.
    const boosted = Math.min(0.9 + Math.min(equivalent, 5) * 0.004, 0.919);
    if (boosted > score) {
      score = boosted;
      via = 'equivalent';
      partial = false;
    }
  }
  return { score: Math.min(1, Math.max(0, score)), via, partial };
}

// ─── Candidates ────────────────────────────────────────────────────────────────────────────────────────────

/** What resolution knows about an extracted entity (from the document). */
export interface ResolutionQuery {
  kind: string;
  /** Extracted sub-type as written ("Person", "Media"…), matched against type keys/labels. */
  type?: string | null;
  names: string[];
  lang?: string | null;
  /** Names of entities the document links it to (relations, events, flows…). */
  linkedNames?: string[];
  /** Other entities named in the same document (weaker context). */
  coMentioned?: string[];
  /** Years its claims in the document refer to. */
  years?: number[];
  identity?: 'confirmed' | 'probable' | 'unidentified';
  incompleteName?: boolean;
}

/** An existing entity, as the claims service loads it for resolution. */
export interface ResolutionCandidate {
  entityId: string;
  kind: string;
  typeKey: string;
  /** Keys of the type and its ancestors (Person → Actor root…). */
  typeChain: string[];
  /** Labels of the same chain, lower case. */
  typeLabels: string[];
  displayName: string;
  names: { value: string; lang: string | null }[];
  /** Names of entities linked to it by current claims (both directions). */
  linkedNames: string[];
  /** Years its current claims refer to. */
  years: number[];
  /** Best vector similarity from the `names` index, when it was a vector hit. */
  vectorScore?: number;
}

export interface MatchEvidence {
  /** Linked in both the document and the graph. */
  sharedLinks: string[];
  /** Named in the same document and linked in the graph. */
  coMentioned: string[];
  sharedYears: number[];
  type: 'same' | 'compatible' | 'conflict' | 'unknown';
}

export interface MatchSuggestion {
  entityId: string;
  displayName: string;
  typeKey: string;
  score: number;
  nameScore: number;
  contextScore: number;
  via: MatchVia;
  /** The query name and the entity name that matched best. */
  queryName: string;
  matchedName: string;
  crossScript: boolean;
  partial: boolean;
  /** Pre-selected in review: score ≥ 0.92, same script and language, a named (not unidentified) entity. */
  preselected: boolean;
  evidence: MatchEvidence;
}

const lc = (s: string) => normalizeName(s);

function typeRelation(q: ResolutionQuery, c: ResolutionCandidate): MatchEvidence['type'] {
  const t = q.type ? lc(q.type) : '';
  if (!t) return 'unknown';
  const keys = c.typeChain.map(k => k.replace(/_/g, ' '));
  if (lc(c.typeKey.replace(/_/g, ' ')) === t || c.typeLabels[0] === t) return 'same';
  if (keys.includes(t) || c.typeLabels.includes(t)) return 'compatible';
  // A person is never an organization (or any of its sub-types), and the reverse.
  const qPerson = t === 'person' || t === 'individual';
  const qOrg =
    t.startsWith('organi') ||
    ['company', 'media', 'ngo', 'party', 'political party', 'government body', 'foundation', 'think tank'].includes(t);
  const cPerson = keys.includes('person');
  const cOrg = keys.includes('organization');
  if ((qPerson && cOrg) || (qOrg && cPerson)) return 'conflict';
  return 'unknown';
}

/** Best matching name pair between the query and the candidate, preferring same-script pairs on near ties. */
function bestName(q: ResolutionQuery, c: ResolutionCandidate, eq?: EquivalentIndex) {
  let same: { m: NameMatch; qn: string; cn: string } | null = null;
  let cross: { m: NameMatch; qn: string; cn: string } | null = null;
  for (const qn of q.names) {
    for (const n of c.names) {
      const m = nameSimilarity(qn, n.value, eq);
      const isCross = isCrossScript({ value: qn, lang: q.lang }, n);
      const slot = isCross ? cross : same;
      if (!slot || m.score > slot.m.score) {
        if (isCross) cross = { m, qn, cn: n.value };
        else same = { m, qn, cn: n.value };
      }
    }
  }
  if (same && (!cross || same.m.score >= cross.m.score - 0.02)) return { ...same, cross: false };
  if (cross) return { ...cross, cross: true };
  return null;
}

/**
 * Blocking keys of a name: the sound and the first letters of each word (and of the words run together). Two
 * names that match at 0.85 or more share one: that score needs a word pair that is the same, a transliteration
 * fit, a sound-alike key or a spelling within Jaro–Winkler 0.9 (a shared start or sound). Initials, partial names
 * and acronyms score below 0.85 and need no key.
 */
function blockingKeys(value: string): readonly string[] {
  return keysMemo(value, () => {
    const w = wordsOf(value);
    const out = new Set<string>();
    for (const x of w.words) {
      if (x.phonetic) out.add(`p:${x.phonetic}`);
      out.add(`l:${x.latin.slice(0, 3)}`);
    }
    if (w.words.length > 1) {
      const joined = w.words.map(x => x.latin).join('');
      out.add(`l:${joined.slice(0, 3)}`);
      const p = phoneticWord(joined);
      if (p) out.add(`p:${p}`);
    }
    return [...out];
  });
}

/**
 * An existing entity's linked names, indexed for the context comparison: a document name is looked up by its
 * folded form first, then compared (nameSimilarity ≥ 0.85) only with the linked names sharing a blocking key.
 * A well-linked entity can have hundreds of linked names, and a document hundreds of entities: comparing every
 * pair in full took seconds of CPU per entity and failed the Matching step on long documents. Confirmed name
 * equivalents still raise a comparison, but only between names sharing a key.
 */
class LinkedNameIndex {
  private readonly exact = new Set<string>();
  private readonly byKey = new Map<string, string[]>();
  constructor(names: readonly string[]) {
    for (const n of new Set(names)) {
      const norm = normalizeName(n);
      if (!norm) continue;
      this.exact.add(norm);
      for (const k of blockingKeys(n)) {
        const list = this.byKey.get(k);
        if (list) list.push(n);
        else this.byKey.set(k, [n]);
      }
    }
  }
  has(name: string, eq?: EquivalentIndex): boolean {
    const norm = normalizeName(name);
    if (!norm) return false;
    if (this.exact.has(norm)) return true;
    const seen = new Set<string>();
    for (const k of blockingKeys(name))
      for (const x of this.byKey.get(k) ?? []) {
        if (seen.has(x)) continue;
        seen.add(x);
        if (nameSimilarity(name, x, eq).score >= 0.85) return true;
      }
    return false;
  }
}

/** Context overlap (0–1) between what the document says around the entity and what the graph holds. */
function contextOf(q: ResolutionQuery, c: ResolutionCandidate, eq?: EquivalentIndex): { score: number; evidence: MatchEvidence } {
  const linked = new LinkedNameIndex(c.linkedNames);
  const sharedLinks = [...new Set((q.linkedNames ?? []).filter(n => linked.has(n, eq)))];
  const coMentioned = [...new Set((q.coMentioned ?? []).filter(n => !sharedLinks.includes(n) && linked.has(n, eq)))];
  const sharedYears = [...new Set((q.years ?? []).filter(y => c.years.includes(y)))].sort();
  const type = typeRelation(q, c);
  let score = Math.min(sharedLinks.length * 0.4, 0.8) + Math.min(coMentioned.length * 0.15, 0.3) + Math.min(sharedYears.length * 0.1, 0.2);
  if (type === 'same') score += 0.1;
  score = Math.min(1, score);
  return { score, evidence: { sharedLinks, coMentioned, sharedYears, type } };
}

/** Scores one existing entity against an extracted one; null when it can't be the same (other kind, no name match). */
export function scoreCandidate(q: ResolutionQuery, c: ResolutionCandidate, eq?: EquivalentIndex): MatchSuggestion | null {
  if (q.kind !== c.kind || !q.names.length) return null;
  const best = bestName(q, c, eq);
  let nameScore = best?.m.score ?? 0;
  let via = best?.m.via ?? 'none';
  // A vector hit with no spelled-out match still counts, weakly (embeddings see meaning, not identity).
  if (c.vectorScore !== undefined && c.vectorScore * 0.8 > nameScore) {
    nameScore = c.vectorScore * 0.8;
    via = 'vector';
  }
  if (nameScore < 0.4) return null;
  const ctx = contextOf(q, c, eq);
  let score = nameScore + (1 - nameScore) * ctx.score * 0.6;
  if (ctx.evidence.type === 'conflict') score -= 0.2;
  score = Math.round(Math.min(1, Math.max(0, score)) * 1000) / 1000;
  const queryName = best?.qn ?? q.names[0] ?? '';
  const matchedName = best?.cn ?? c.displayName;
  const crossScript = best ? best.cross : isCrossScript({ value: queryName, lang: q.lang }, { value: matchedName, lang: null });
  const preselected =
    score >= PRESELECT_THRESHOLD && !crossScript && q.identity !== 'unidentified' && !q.incompleteName && ctx.evidence.type !== 'conflict';
  return {
    entityId: c.entityId,
    displayName: c.displayName,
    typeKey: c.typeKey,
    score,
    nameScore: Math.round(nameScore * 1000) / 1000,
    contextScore: Math.round(ctx.score * 1000) / 1000,
    via,
    queryName,
    matchedName,
    crossScript,
    partial: best?.m.partial ?? false,
    preselected,
    evidence: ctx.evidence,
  };
}

/**
 * Top suggestions for an extracted entity, best first (at most MAX_SUGGESTIONS, score ≥ SUGGEST_THRESHOLD).
 * Only the first may be pre-selected, and only if it is clearly ahead of the runner-up (a near tie is a choice
 * for the reviewer).
 */
export function rankCandidates(q: ResolutionQuery, pool: readonly ResolutionCandidate[], eq?: EquivalentIndex): MatchSuggestion[] {
  if (q.identity === 'unidentified') return [];
  const out = pool
    .map(c => scoreCandidate(q, c, eq))
    .filter((s): s is MatchSuggestion => !!s && s.score >= SUGGEST_THRESHOLD)
    .sort(
      (a, b) =>
        b.score - a.score ||
        b.contextScore - a.contextScore ||
        Number(a.crossScript) - Number(b.crossScript) ||
        a.entityId.localeCompare(b.entityId),
    )
    .slice(0, MAX_SUGGESTIONS);
  const first = out[0];
  out.forEach((s, i) => {
    if (i > 0) s.preselected = false;
  });
  // Not clearly the one: a second strong candidate (a namesake), a near tie, or a plausible other entity (in any
  // script) that shares more context with the document. The reviewer picks.
  if (
    first?.preselected &&
    out.some(
      (s, i) =>
        i > 0 &&
        (s.score >= PRESELECT_THRESHOLD ||
          s.score >= first.score - 0.03 ||
          (s.score >= CONFIRM_THRESHOLD && s.contextScore > first.contextScore)),
    )
  )
    first.preselected = false;
  return out;
}

/** Type key, ancestor keys and lower-case labels of an entity type, as `ResolutionCandidate` needs them. */
export function typeInfo(
  types: readonly { id: string; key: string; label: string; parentTypeId: string | null }[],
  typeId: string,
): Pick<ResolutionCandidate, 'typeKey' | 'typeChain' | 'typeLabels'> {
  const chain: (typeof types)[number][] = [];
  let t = types.find(x => x.id === typeId);
  while (t && chain.length < 10 && !chain.includes(t)) {
    chain.push(t);
    const parent: string | null = t.parentTypeId;
    t = parent ? types.find(x => x.id === parent) : undefined;
  }
  return { typeKey: chain[0]?.key ?? '', typeChain: chain.map(x => x.key), typeLabels: chain.map(x => lc(x.label)) };
}

/** Year numbers mentioned in dates as written ("2021-10-06", "since 2019", "2017–2024"). */
export function yearsIn(values: readonly (string | null | undefined)[]): number[] {
  const out = new Set<number>();
  for (const v of values) for (const m of v?.matchAll(/\b(1[89]\d\d|20\d\d)\b/g) ?? []) out.add(Number(m[1]));
  return [...out].sort();
}
